export interface MarketingAttribution {
  landingPath: string;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
}

const STORAGE_KEY = 'washed:first-touch:v1';
const PATH_MAX = 256;
const REFERRER_MAX = 256;
const UTM_MAX = 160;

function clean(value: string | null | undefined, max: number): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, max) : undefined;
}

function cleanReferrer(raw: string): string | undefined {
  if (!raw) return undefined;
  try {
    const url = new URL(raw);
    return clean(url.origin, REFERRER_MAX);
  } catch {
    return undefined;
  }
}

function readCurrentAttribution(): MarketingAttribution | undefined {
  if (typeof window === 'undefined') return undefined;

  const url = new URL(window.location.href);
  const attribution: MarketingAttribution = {
    landingPath: clean(url.pathname || '/', PATH_MAX) ?? '/',
  };

  const referrer = typeof document !== 'undefined' ? cleanReferrer(document.referrer) : undefined;
  const utmSource = clean(url.searchParams.get('utm_source'), UTM_MAX);
  const utmMedium = clean(url.searchParams.get('utm_medium'), UTM_MAX);
  const utmCampaign = clean(url.searchParams.get('utm_campaign'), UTM_MAX);
  const utmContent = clean(url.searchParams.get('utm_content'), UTM_MAX);
  const utmTerm = clean(url.searchParams.get('utm_term'), UTM_MAX);

  if (referrer) attribution.referrer = referrer;
  if (utmSource) attribution.utmSource = utmSource;
  if (utmMedium) attribution.utmMedium = utmMedium;
  if (utmCampaign) attribution.utmCampaign = utmCampaign;
  if (utmContent) attribution.utmContent = utmContent;
  if (utmTerm) attribution.utmTerm = utmTerm;

  return attribution;
}

function isStoredAttribution(value: unknown): value is MarketingAttribution {
  return (
    typeof value === 'object' &&
    value !== null &&
    'landingPath' in value &&
    typeof (value as { landingPath?: unknown }).landingPath === 'string'
  );
}

/**
 * Capture the first page/referrer/UTMs for this browser session.
 *
 * We intentionally store only the WASHED landing pathname (no query string)
 * and only the referrer's origin/domain (no path, query, or hash). This is
 * enough for acquisition-channel attribution without persisting arbitrary URL
 * values that could contain personal data or opaque access tokens.
 */
export function captureMarketingAttribution(): MarketingAttribution | undefined {
  const current = readCurrentAttribution();
  if (!current || typeof window === 'undefined') return current;

  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      if (isStoredAttribution(parsed)) return parsed;
    }
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch {
    // Storage can be disabled in hardened/privacy browsing modes. Attribution is
    // best-effort; signup must continue even when storage is unavailable.
  }

  return current;
}

export function getMarketingAttribution(): MarketingAttribution | undefined {
  return captureMarketingAttribution();
}
